import { useCallback, useEffect, useState } from "react";
import { Coins, Wrench, Download, Droplets, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { api } from "../lib/api";

const fmt = (n) => Number(n || 0).toLocaleString("en", { maximumFractionDigits: 2 });
const months = () => Array.from({ length: 12 }, (_, i) => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - i); return d.toISOString().slice(0, 7); });

export const WorkshopReport = () => {
  const [month, setMonth] = useState(months()[0]);
  const [data, setData] = useState(null);
  const load = useCallback(() => api.workshopReport(month).then(setData).catch(() => setData(null)), [month]);
  useEffect(() => { load(); }, [load]);
  const xlsx = async () => {
    try { const b = await api.workshopReportXlsx(month); const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = `تقرير_الورشة_${month}.xlsx`; a.click(); }
    catch { toast.error("تعذّر التصدير"); }
  };
  const th = "px-4 py-2.5 text-right whitespace-nowrap font-medium";
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="workshop-report">
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
        <div className="flex items-center gap-1.5 font-heading text-sm font-bold text-slate-800"><Wrench className="h-4 w-4 text-blue-600" /> تقرير الورشة الشهري</div>
        <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={xlsx} disabled={!data?.rows?.length} data-testid="workshop-report-xlsx"><Download className="h-3.5 w-3.5" /> Excel</Button>
        <select value={month} onChange={(e) => setMonth(e.target.value)} className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs" data-testid="workshop-report-month">
          {months().map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        </div>
      </div>
      {!data?.rows?.length ? <p className="px-4 py-6 text-center text-xs text-slate-400" data-testid="workshop-report-empty">لا توجد صيانة من الورشة في هذا الشهر.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-right text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500"><tr>
              <th className={th}>المركبة</th><th className={th}>الزيت</th><th className={th}>القطع</th><th className={th}>الزيارات</th><th className={th}>الإجمالي</th>
            </tr></thead>
            <tbody className="font-mono-nums">
              {data.rows.map((r) => (
                <tr key={r.vehicle_number} className="border-t border-slate-100" data-testid={`workshop-row-${r.vehicle_number}`}>
                  <td className="px-4 py-2.5 font-sans"><div className="font-semibold text-slate-800">{r.vehicle_number}</div>{r.driver && <div className="text-[11px] text-slate-400">{r.driver}</div>}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-blue-600">{fmt(r.oil_cost)} <span className="text-[10px] text-slate-400">({r.oil_count})</span></td>
                  <td className="px-4 py-2.5 text-amber-600" title={r.parts.join("، ")}>{fmt(r.parts_cost)} <span className="text-[10px] text-slate-400">({r.parts_count})</span></td>
                  <td className="px-4 py-2.5">{r.visits}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 font-bold text-slate-800">{fmt(r.total)} ⃁</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr className="border-t-2 border-slate-200 bg-slate-50 font-bold font-mono-nums">
              <td className="px-4 py-2.5 font-sans">الإجمالي ({data.rows.length} مركبة)</td>
              <td className="px-4 py-2.5 text-blue-600">{fmt(data.totals.oil_cost)}</td>
              <td className="px-4 py-2.5 text-amber-600">{fmt(data.totals.parts_cost)}</td>
              <td className="px-4 py-2.5">{data.totals.visits}</td>
              <td className="whitespace-nowrap px-4 py-2.5 text-emerald-600" data-testid="workshop-report-total"><Coins className="inline h-3.5 w-3.5" /> {fmt(data.totals.total)} ⃁</td>
            </tr></tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export const OilDueCard = () => {
  const [d, setD] = useState(null); const [busy, setBusy] = useState(false);
  useEffect(() => { api.oilDue().then(setD).catch(() => {}); }, []);
  const send = async () => {
    setBusy(true);
    try { const r = await api.oilDueSend(); r.sent ? toast.success(`تم إرسال التنبيه (${r.count} سيارة)`) : toast.info(r.count ? "ما تم الإرسال، تأكد من ربط تيليجرام في الإعدادات" : "لا توجد سيارات قريبة من التغيير"); }
    catch { toast.error("تعذّر الإرسال"); } finally { setBusy(false); }
  };
  if (!d) return null;
  return (
    <div className="rounded-2xl border border-amber-200 bg-white shadow-sm" data-testid="oil-due-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-100 px-4 py-3">
        <div className="flex items-center gap-1.5 font-heading text-sm font-bold text-slate-800"><Droplets className="h-4 w-4 text-amber-600" /> سيارات قربت من تغيير الزيت <span className="text-[11px] font-normal text-slate-400">(حسب تقرير المسافات · قبل الحد بـ {fmt(d.alert_km)} كم)</span></div>
        <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={send} disabled={busy} data-testid="oil-due-send"><Send className="h-3.5 w-3.5" /> إرسال تيليجرام</Button>
      </div>
      {!d.rows.length ? <p className="px-4 py-4 text-center text-xs text-slate-400" data-testid="oil-due-empty">لا توجد سيارات قريبة من التغيير حالياً.</p> : (
        <div className="divide-y divide-slate-100">{d.rows.map((r) => (
          <div key={r.vehicle_number} className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid={`oil-due-${r.vehicle_number}`}>
            <div><div className="font-semibold">{r.vehicle_number}</div><div className="text-[11px] text-slate-400">{r.driver ? `${r.driver} · ` : ""}آخر تغيير {fmt(r.last_odometer)} + مشت {fmt(r.driven_km)} كم = ~{fmt(r.estimated)} · التغيير عند {fmt(r.next_due)}{r.source !== "distances" ? " (تقدير)" : ""}</div></div>
            <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${r.remaining < 0 ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{r.remaining < 0 ? `تجاوز ${fmt(-r.remaining)} كم` : `باقي ${fmt(r.remaining)} كم`}</span>
          </div>
        ))}</div>
      )}
    </div>
  );
};
